// WAT-11663: two CMS entries can be the SAME product reached two different ways — Disney+ appears
// once on its own (MyKids) and once as the Disney+ half of the Duo bundle, with the same logo AND the
// same resolved name, so the page looked like it repeated a subscription. Content owners mark such
// entries with a shared `productGroup` value and they collapse into one row.
//
// The grouping key is the CMS field ONLY — never the logo URL and never the resolved name. HBO Max
// Standard and HBO Max Basic With Ads share one logo but are genuinely different products and must
// stay two rows; and the resolved name comes from Epoch's svodNameMapping, which can change without a
// code release, so name equality would silently split or merge rows.
const mergeIndicatorLabels = (current, incoming) => {
  const parts = [...`${current || ""}`.split(","), ...`${incoming || ""}`.split(",")]
    .map(part => part?.trim())
    .filter(Boolean);
  return [...new Set(parts)].join(", ");
};

export const mergeSameProductRows = rows => (rows || []).reduce((merged: any[], row: any) => {
  // Case-folded: productGroup is hand-authored free text in the CMS, so "Disney+" vs "disney+"
  // must group. Must stay identical to collapseSameProductSvods in lib/helper/svod-utils.ts.
  const group = `${row?.productGroup || ""}`.trim().toLowerCase();
  const target = group ? merged.find(kept => `${kept?.productGroup || ""}`.trim().toLowerCase() === group) : undefined;
  // No productGroup value (every other entry) or the first row of its group: keep it untouched.
  if (!target) {
    // Copy grouped rows so the merge below never mutates the caller's objects — matches the
    // purity contract of collapseSameProductSvods (lib/helper/svod-utils.ts). Ungrouped rows are
    // never written to, so they pass through by reference.
    merged.push(group ? { ...row } : row);
    return merged;
  }
  // Same product: a checkmark under ANY grouped entry means that column includes the product, and the
  // hover text lists every package it came from.
  // Size the merge off the LONGER of the two, never the survivor's array. Iterating the survivor
  // would silently drop the incoming row's trailing checkmarks — which fails in the one direction
  // that misinforms the customer: telling them a package does NOT include a subscription it does.
  const columnCount = Math.max(target?.svodIndicators?.length || 0, row?.svodIndicators?.length || 0);
  const labelCount = Math.max(target?.indicatorLabels?.length || 0, row?.indicatorLabels?.length || 0);
  target.svodIndicators = Array.from({ length: columnCount }, (_v, idx) => !!(target?.svodIndicators?.[idx] || row?.svodIndicators?.[idx]));
  target.indicatorLabels = Array.from({ length: labelCount }, (_v, idx) => mergeIndicatorLabels(target?.indicatorLabels?.[idx], row?.indicatorLabels?.[idx]));
  target.packageListSystem = [...new Set([...(target?.packageListSystem || []), ...(row?.packageListSystem || [])])];
  return merged;
}, []);

export const getSvodData = async (displayPackages, packageConfig, otherData) => {
  try {
    const {
      svodAccordionName,
      embeddedSvods,
      embeddedIncludedSvods,
      getOffersGenreData,
      maxColumns,
      columnSelections,
      miniSelectionsByColumn
    } = otherData || {};
    const svodChannelData = getSvodsChannels(packageConfig, embeddedSvods || [], embeddedIncludedSvods || {}, getOffersGenreData || {});

    const enabledSvods = (svodChannelData || []).filter(svod => {
      const disableFlag = svod?.cmpPkgData?.disable;
      return disableFlag !== true;
    });

    const matchingSvods = enabledSvods.filter(svod => Array.isArray(svod?.packageListSystem) &&
            svod.packageListSystem.some(pkgId => displayPackages.some(pkg => pkg.id === pkgId)));

    const sortedMatchingSvods = matchingSvods.sort((a, b) => {
      const nameA = (a?.channelNameLabel || "").toString().toLowerCase();
      const nameB = (b?.channelNameLabel || "").toString().toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });

    const rows = sortedMatchingSvods.map(svod => {
      const base = svod.cmpPkgData || svod;
      const svodIndicators = [];
      const indicatorLabels = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < maxColumns; i++) {
        // Check genre and mini packs for this column
        const sel = columnSelections && columnSelections[i];
        let found = false;
        const matchedPkgNames: string[] = [];
        const genreIds = sel?.genre || [];
        const signatureIds = sel?.signature || [];
        const miniIds = miniSelectionsByColumn && miniSelectionsByColumn[i] ? miniSelectionsByColumn[i] : [];
        const allPkgIds = [...genreIds, ...signatureIds, ...miniIds];

        if (Array.isArray(svod.packageListSystem)) {
          allPkgIds.forEach(pkgId => {
            const matches = svod.packageListSystem.some(p => typeof p === 'string' && p.toUpperCase() === pkgId.toUpperCase());
            if (matches) {
              found = true;
              const pkg = (packageConfig || []).find(p => p.id?.toUpperCase() === pkgId.toUpperCase() || p.queryparam?.toUpperCase() === pkgId.toUpperCase());
              matchedPkgNames.push(pkg?.displayLabel || pkg?.packageName || pkgId);
            }
          });
        }

        svodIndicators.push(!!found);
        indicatorLabels.push(found ? matchedPkgNames.join(", ") : "");
      }
      return {
        ...base,
        // WAT-11085: SvodRow renders `name`; use the server-resolved label (channelNameLabel) so the
        // svod name/tooltip shows even when cmpPkgData lacks a name.
        name: svod?.channelNameLabel,
        // WAT-11663: `base` is cmpPkgData, so the entry's TOP-LEVEL productGroup would be dropped by
        // the spread. Carry it explicitly — it is the grouping key mergeSameProductRows reads.
        productGroup: svod?.productGroup,
        packageListSystem: svod.packageListSystem,
        svodIndicators,
        indicatorLabels
      };
    });
    return {
      label: svodAccordionName,
      type: "svod",
      rows: mergeSameProductRows(rows)
    };
  } catch (_error) {
    return {
      label: otherData?.svodAccordionName || "Subscriptions",
      type: "svod",
      rows: []
    }
  }
}

export function getMatchingSvods(svodChannelData, displayPackages) {
  const displayPackageIds = new Set(displayPackages.map(pkg => pkg.id));
  const matches = svodChannelData
    .filter(svod => {
      const isMatch = Array.isArray(svod.packageListSystem) &&
                svod.packageListSystem.some(pkgId => displayPackageIds.has(pkgId));
      return isMatch;
    })
    .map(svod => {
      if (!svod.cmpPkgData) {
        console.warn("SVOD matched but cmpPkgData is undefined:", svod);
      }
      return svod.cmpPkgData;
    });
  return matches;
}

export const getSvodsChannels = (packageConfig, embeddedSvods, embeddedIncludedSvods, getOffersGenreData) => {
  const filteredPkgs = packageConfig?.reduce((acc, pkg) => {
    const pkgId = pkg?.id?.replace(/ATT_TV|_/g, '')?.toLowerCase?.();
    acc[pkgId] = pkg?.id;
    return acc;
  }, {});
  const svodData = embeddedSvods?.map((item: any) => {
    if (!item?.agentToolData?.disable) {
      const packageListSystem = [];
      const offerCode = item?.offerCode;

      Object?.keys(filteredPkgs)?.forEach?.((key: string) => {
        embeddedIncludedSvods?.[key]?.forEach?.((svod: any) => {
          if (svod?.code === offerCode && filteredPkgs?.[key]) {
            packageListSystem?.push(filteredPkgs?.[key]);
          }
        });
      });
      Object.entries(getOffersGenreData?.svodMapping || {}).forEach(([key, svdMap]) => {
        Array.isArray(svdMap) && svdMap.includes(offerCode) && packageListSystem?.push(key);
      })
      return {
        ...item,
        packageListSystem,
        // WAT-11085: name is resolved server-side (attachResolvedSvodNames) into channelNameLabel.
        // Keep a defensive fallback to displayName/name so any producer path that hasn't run the
        // resolver (App Router parity, sandbox, stale SSR spanning a deploy) never renders blank.
        channelNameLabel: item?.channelNameLabel || item?.displayName || item?.name
      };
    }
    return undefined;
  });
  return svodData;
}
